/**
 * @page /portal/news, /portal/news/:id
 * @description Portal-News — Firmennachrichten lesen, filtern und verwalten.
 *   Admins/Manager koennen News erstellen, bearbeiten und loeschen.
 * @features
 *   - Kategorie-Filter (allgemein, wichtig, schichtinfo, hr, events)
 *   - Detail-Ansicht mit Lesebestaetigung
 *   - CRUD fuer Admins/Manager (erstellen, bearbeiten, loeschen)
 *   - Anhang-Upload und Pinning
 * @api GET /api/portal/news, GET /api/portal/news/:id
 * @api POST /api/portal/news, PUT /api/portal/news/:id, DELETE /api/portal/news/:id
 * @api POST /api/portal/news/:id/gelesen
 * @roles mitarbeiter, admin, administrator, manager, ops_manager, teamleiter
 * @i18n portal.news
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Pin, Paperclip, Newspaper, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '../components/ui';
import ConfirmDialog from '../components/ConfirmDialog';

const API = import.meta.env.VITE_API_URL || '';

const KATEGORIEN_KEYS = [
  { value: 'alle', tKey: 'portal.news.catAll' },
  { value: 'allgemein', tKey: 'portal.news.catGeneral' },
  { value: 'wichtig', tKey: 'portal.news.catImportant' },
  { value: 'schichtinfo', tKey: 'portal.news.catShiftInfo' },
  { value: 'hr', tKey: 'portal.news.catHR' },
  { value: 'events', tKey: 'portal.news.catEvents' },
];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function MaPortalNews() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { id: detailId } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kategorie, setKategorie] = useState('alle');
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ titel: '', inhalt: '', kategorie: 'allgemein', wichtig: false, gepinnt: false });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const isAdmin = ['admin', 'administrator', 'manager', 'ops_manager', 'teamleiter'].includes(user?.rolle);

  const loadNews = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/news?kategorie=${kategorie}`, { credentials: 'include' });
      if (res.ok) setNews(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [kategorie]);

  useEffect(() => { loadNews(); }, [loadNews]);

  // Load detail wenn URL-Parameter vorhanden
  useEffect(() => {
    if (detailId) {
      loadDetail(detailId);
    } else {
      setDetail(null);
    }
  }, [detailId]);

  const loadDetail = async (nid) => {
    try {
      const res = await fetch(`${API}/api/portal/news/${nid}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setDetail(d);
        // Als gelesen markieren
        if (!d.gelesen) {
          fetch(`${API}/api/portal/news/${nid}/gelesen`, { method: 'POST', credentials: 'include' }).catch(() => {});
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    if (!formData.titel || !formData.inhalt) return;
    setSaving(true);
    try {
      const url = editId ? `${API}/api/portal/news/${editId}` : `${API}/api/portal/news`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setShowForm(false);
        setEditId(null);
        setFormData({ titel: '', inhalt: '', kategorie: 'allgemein', wichtig: false, gepinnt: false });
        loadNews();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (nid) => {
    setConfirmDialog({
      title: t('portal.news.delete'),
      message: t('portal.news.confirmDelete'),
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await fetch(`${API}/api/portal/news/${nid}`, { method: 'DELETE', credentials: 'include' });
          loadNews();
          if (detail?.id === nid) {
            setDetail(null);
            navigate('/portal/news');
          }
        } catch (err) {
          console.error(err);
        }
      },
    });
  };

  const handleEdit = (item) => {
    setFormData({
      titel: item.titel,
      inhalt: item.inhalt,
      kategorie: item.kategorie || 'allgemein',
      wichtig: item.wichtig || false,
      gepinnt: item.gepinnt || false,
    });
    setEditId(item.id);
    setShowForm(true);
  };

  // Detail-Ansicht
  if (detail) {
    return (
      <div className="p-page">
        <button
          onClick={() => { setDetail(null); navigate('/portal/news'); }}
          className="text-sm text-[var(--text-tertiary)] hover:text-content mb-4 inline-flex items-center gap-1"
        >
          {t('portal.news.backToNews')}
        </button>

        <div
          className={`bg-surface-card border rounded-card p-6 ${detail.wichtig ? 'border-red-500/50' : 'border-line'}`}
          style={{ borderTopColor: '#338edc', borderTopWidth: 3 }}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {detail.gepinnt && <Pin className="w-3.5 h-3.5 text-primary-400" />}
                {detail.wichtig && (
                  <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-[10px] font-bold uppercase">{t('portal.news.important')}</span>
                )}
                <span className="px-2 py-0.5 rounded bg-surface-inset text-[var(--text-tertiary)] text-[10px] uppercase">
                  {detail.kategorie}
                </span>
              </div>
              <h1 className="text-xl font-bold text-content">{detail.titel}</h1>
            </div>
            {isAdmin && (
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => handleEdit(detail)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-500/10 text-primary-400 hover:bg-primary-500/20">
                  {t('portal.news.edit')}
                </button>
                <button onClick={() => handleDelete(detail.id)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20">
                  {t('portal.news.delete')}
                </button>
              </div>
            )}
          </div>

          <div className="text-xs text-[var(--text-tertiary)] mb-4">
            {t('portal.news.by', { firstName: detail.autor_vorname, lastName: detail.autor_nachname })} · {formatDateTime(detail.veroeffentlicht_am)}
          </div>

          <div className="text-sm text-content-secondary leading-relaxed whitespace-pre-wrap">
            {detail.inhalt}
          </div>

          {detail.anhang_name && (
            <div className="mt-4 pt-4 border-t border-line">
              <a
                href={detail.anhang_url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-inset text-sm text-[var(--text-tertiary)] hover:text-content"
              >
                <Paperclip className="w-3.5 h-3.5 inline mr-1" />{detail.anhang_name}
              </a>
            </div>
          )}

          {/* Lesebestätigungen (Admin/Manager) */}
          {isAdmin && detail.leser?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-line">
              <h3 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">
                {t('portal.news.readBy', { n: detail.leser.length })}
              </h3>
              <div className="flex flex-wrap gap-2">
                {detail.leser.map((l, i) => (
                  <span key={i} className="px-2 py-1 rounded bg-surface-inset text-xs text-[var(--text-tertiary)]">
                    {l.vorname} {l.nachname} · {formatDateTime(l.gelesen_am)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ConfirmDialog */}
        <ConfirmDialog
          open={!!confirmDialog}
          title={confirmDialog?.title}
          message={confirmDialog?.message}
          confirmLabel={t('portal.news.delete')}
          cancelLabel={t('common.cancel')}
          variant="danger"
          onConfirm={confirmDialog?.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      </div>
    );
  }

  return (
    <div className="p-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-section">
        <PageHeader title={t('portal.news.title')} />
        {isAdmin && (
          <button
            onClick={() => { setShowForm(true); setEditId(null); setFormData({ titel: '', inhalt: '', kategorie: 'allgemein', wichtig: false, gepinnt: false }); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors"
          >
            {t('portal.news.newNews')}
          </button>
        )}
      </div>

      {/* Kategorie-Filter — SKILL-design tab-bar pattern */}
      <div className="mb-section">
        <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 flex-wrap">
          {KATEGORIEN_KEYS.map(k => (
            <button
              key={k.value}
              onClick={() => setKategorie(k.value)}
              className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
                kategorie === k.value
                  ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {t(k.tKey)}
            </button>
          ))}
        </div>
      </div>

      {/* News-Formular */}
      {showForm && (
        <div className="bg-surface-card border border-primary-500/30 rounded-card p-5 mb-section">
          <h3 className="text-sm font-bold text-content mb-4">{editId ? t('portal.news.editNews') : t('portal.news.createNews')}</h3>
          <div className="space-y-3">
            <input
              type="text"
              placeholder={t('portal.news.titlePlaceholder')}
              value={formData.titel}
              onChange={e => setFormData(prev => ({ ...prev, titel: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc]"
            />
            <textarea
              placeholder={t('portal.news.contentPlaceholder')}
              rows={6}
              value={formData.inhalt}
              onChange={e => setFormData(prev => ({ ...prev, inhalt: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc] resize-y"
            />
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={formData.kategorie}
                onChange={e => setFormData(prev => ({ ...prev, kategorie: e.target.value }))}
                className="px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content focus:outline-none"
              >
                {KATEGORIEN_KEYS.filter(k => k.value !== 'alle').map(k => (
                  <option key={k.value} value={k.value}>{t(k.tKey)}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.wichtig}
                  onChange={e => setFormData(prev => ({ ...prev, wichtig: e.target.checked }))}
                  className="rounded"
                />
                {t('portal.news.important')}
              </label>
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.gepinnt}
                  onChange={e => setFormData(prev => ({ ...prev, gepinnt: e.target.checked }))}
                  className="rounded"
                />
                {t('portal.news.pinned')}
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={saving || !formData.titel || !formData.inhalt}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
              >
                {saving ? t('portal.news.saving') : editId ? t('portal.news.update') : t('portal.news.publish')}
              </button>
              <button
                onClick={() => { setShowForm(false); setEditId(null); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-inset text-[var(--text-tertiary)] hover:text-content transition-colors"
              >
                {t('portal.news.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* News Feed */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse bg-surface-card border border-line rounded-card p-5">
              <div className="h-4 bg-surface-inset rounded w-2/3 mb-3" />
              <div className="h-3 bg-surface-inset rounded w-full mb-2" />
              <div className="h-3 bg-surface-inset rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : news.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
            <Newspaper size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">
            {t('portal.news.noNews')}
          </h3>
        </div>
      ) : (
        <div className="space-y-3">
          {news.map(item => (
            <div
              key={item.id}
              onClick={() => navigate(`/portal/news/${item.id}`)}
              className={`bg-surface-card border rounded-card p-5 cursor-pointer hover:border-line-hover transition-all group ${
                item.wichtig ? 'border-red-500/40 hover:border-red-500/60' : 'border-line'
              }`}
              style={item.gepinnt ? { borderTopColor: '#338edc', borderTopWidth: 2 } : undefined}
            >
              <div className="flex items-start gap-3">
                {/* Gelesen-Indikator */}
                <div className="flex-shrink-0 mt-1.5">
                  {!item.gelesen ? (
                    <span className="w-2.5 h-2.5 rounded-full bg-[#338edc] inline-block" />
                  ) : (
                    <span className="w-2.5 h-2.5 rounded-full bg-transparent inline-block" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {item.gepinnt && <Pin className="w-3 h-3 text-primary-400" />}
                    {item.wichtig && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-[9px] font-bold uppercase">{t('portal.news.important')}</span>
                    )}
                    <span className="px-1.5 py-0.5 rounded bg-surface-inset text-[var(--text-tertiary)] text-[9px] uppercase">
                      {item.kategorie}
                    </span>
                  </div>

                  <h3 className="text-sm font-semibold text-content group-hover:text-primary-400 transition-colors">
                    {item.titel}
                  </h3>

                  <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">
                    {item.inhalt}
                  </p>

                  <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--text-tertiary)]">
                    <span>{item.autor_vorname} {item.autor_nachname}</span>
                    <span>·</span>
                    <span>{formatDate(item.veroeffentlicht_am)}</span>
                    {isAdmin && (
                      <>
                        <span>·</span>
                        <span>{t('portal.news.nRead', { n: item.gelesen_count })}</span>
                      </>
                    )}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex-shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={e => { e.stopPropagation(); handleEdit(item); }}
                      className="p-1.5 rounded hover:bg-surface-hover text-[var(--text-tertiary)] hover:text-primary-400"
                      title={t('portal.news.edit')}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); handleDelete(item.id); }}
                      className="p-1.5 rounded hover:bg-surface-hover text-[var(--text-tertiary)] hover:text-red-400"
                      title={t('portal.news.delete')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ConfirmDialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={t('portal.news.delete')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
